/**
 * Error Service for centralized error handling
 *
 * This service handles error logging, reporting, and provides recovery mechanisms
 * for the application. Errors can be logged to the console, sent to a monitoring
 * service, and optionally reported to an API endpoint.
 */

import { API_BASE_URL } from '../api/apiConfig'

// Configuration for error reporting
const ERROR_CONFIG = {
  // Whether to send errors to a backend API
  REPORT_TO_API: import.meta.env.VITE_REPORT_ERRORS === 'true',
  // API endpoint for error reporting - use absolute URL for security
  ERROR_API_ENDPOINT:
    import.meta.env.VITE_ERROR_API_ENDPOINT || `${String(API_BASE_URL)}/api/v1/errors`,
  // Minimum logging level
  MIN_LOG_LEVEL: import.meta.env.VITE_MIN_LOG_LEVEL || 'error',
}

// Error levels
const ERROR_LEVELS = {
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
  CRITICAL: 'critical',
}

export const errorService = {
  /**
   * Log an error
   * @param {Error} error - The error object
   * @param {Object} errorInfo - Additional error information
   */
  logError(error, errorInfo = {}) {
    // Skip logging if below minimum level
    if (!this._shouldLog(ERROR_LEVELS.ERROR)) return

    // Log to console
    this._logToConsole(error, errorInfo, ERROR_LEVELS.ERROR)

    // Report to monitoring service if configured
    if (ERROR_CONFIG.REPORT_TO_API) {
      this._reportToApi(error, errorInfo, ERROR_LEVELS.ERROR)
    }
  },

  /**
   * Get formatted error message for display
   * @param {Error} error - The error object
   * @returns {string} Formatted error message
   */
  getErrorMessage(error) {
    if (!error) return 'An unknown error occurred'

    // Extract message from different error types
    if (typeof error === 'string') return error
    if (error.message) return error.message
    if (error.statusText) return `${error.status}: ${error.statusText}`

    return 'An unexpected error occurred'
  },

  /**
   * Check if error should be logged based on minimum level
   * @param {string} level - Error level
   * @returns {boolean} Whether to log the error
   * @private
   */
  _shouldLog(level) {
    const levels = Object.values(ERROR_LEVELS)
    const minLevelIndex = levels.indexOf(ERROR_CONFIG.MIN_LOG_LEVEL)
    const currentLevelIndex = levels.indexOf(level)

    return currentLevelIndex >= minLevelIndex
  },

  /**
   * Log to console with appropriate level
   * @param {Error} error - The error object
   * @param {Object} errorInfo - Additional error information
   * @param {string} level - Error level
   * @private
   */
  _logToConsole(error, errorInfo, level) {
    const logData = {
      timestamp: new Date().toISOString(),
      error: error.toString(),
      stack: error.stack,
      ...errorInfo,
    }

    switch (level) {
      case ERROR_LEVELS.DEBUG:
        break
      case ERROR_LEVELS.INFO:
        break
      case ERROR_LEVELS.WARN:
        if (import.meta.env.DEV) console.warn('WARNING:', logData)
        break
      case ERROR_LEVELS.CRITICAL:
        console.error('CRITICAL ERROR:', logData)
        break
      default:
        console.error('ERROR:', logData)
    }
  },

  /**
   * Report error to API endpoint
   * @param {Error} error - The error object
   * @param {Object} errorInfo - Additional error information
   * @param {string} level - Error level
   * @private
   */
  async _reportToApi(error, errorInfo, level) {
    try {
      // Prepare error report data
      const errorReport = {
        timestamp: new Date().toISOString(),
        level,
        message: error.message || error.toString(),
        stack: error.stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        ...errorInfo,
      }

      // Send error to API - only in production if enabled
      // In development, errors are logged to console via logger
      if (import.meta.env.PROD && ERROR_CONFIG.REPORT_TO_API) {
        await fetch(ERROR_CONFIG.ERROR_API_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(errorReport),
          // Don't wait more than 3 seconds to report error
          signal: AbortSignal.timeout(3000),
        })
      }
    } catch (reportingError) {
      // Don't throw from the error reporter to avoid infinite loops
      // Use logger instead of console.error
      if (import.meta.env.DEV) {
        console.error('Failed to report error:', reportingError)
      }
    }
  },
}
